import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2, Download, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Loader } from "../components/ProtectedRoute";
import { EntryMoodTags } from "../components/EntryMoodTags";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const SPEEDS = [1, 1.25, 1.5];

export default function EntryDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [entry, setEntry] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [speed, setSpeed] = useState(1);
  const [audioEl, setAudioEl] = useState(null);

  useEffect(() => {
    let url;
    api.get(`/entries/${id}`).then((r) => setEntry(r.data)).catch(() => nav("/journal"));
    api.get(`/entries/${id}/audio`, { responseType: "blob" }).then((r) => {
      url = URL.createObjectURL(r.data);
      setAudioUrl(url);
    }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [id, nav]);

  useEffect(() => {
    if (audioEl) audioEl.playbackRate = speed;
  }, [speed, audioEl]);

  const remove = async () => {
    try {
      await api.delete(`/entries/${id}`);
      toast.success(t("d_deleted"));
      nav("/journal");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!entry) return <Loader />;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12 sm:py-16 fade-up">
      <Link to="/journal" data-testid="entry-detail-back-btn" className="inline-flex items-center gap-2 text-sm text-[#8E9BB4] hover:text-[#E5C158] mb-10">
        <ArrowLeft size={16} /> {t("d_back")}
      </Link>
      <div className="flex items-center gap-3 text-xs text-[#8E9BB4] mb-4">
        <span>{new Date(entry.created_at).toLocaleString(lang === "fr" ? "fr-FR" : "en-GB", { dateStyle: "full", timeStyle: "short" })}</span>
        <span className="uppercase tracking-wider border border-[#E5C158]/30 text-[#E5C158] rounded-full px-2 py-0.5" data-testid="entry-detail-language">{entry.language}</span>
        {entry.voice_effect && entry.voice_effect !== "normal" && (
          <span className="border border-white/15 text-[#C2C9DB] rounded-full px-2 py-0.5" data-testid="entry-detail-voice-effect">{t(`fx_${entry.voice_effect}`)}</span>
        )}
        <Lock size={12} className="text-[#E5C158]/70" />
      </div>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#F4F6FA] font-semibold leading-tight" data-testid="entry-detail-title">{entry.title}</h1>
      <EntryMoodTags entry={entry} onUpdated={setEntry} />

      <div className="glass-card p-5 sm:p-6 mt-10">
        {audioUrl ? (
          <audio ref={setAudioEl} controls src={audioUrl} className="w-full" data-testid="entry-detail-audio-player" />
        ) : (
          <div className="h-12 rounded-full bg-white/5 animate-pulse" />
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <div className="flex gap-2">
            {SPEEDS.map((s) => (
              <button key={s} data-testid={`entry-detail-speed-${s}`} onClick={() => setSpeed(s)}
                className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${speed === s ? "bg-[#E5C158] text-[#060A12] border-[#E5C158]" : "border-white/10 text-[#C2C9DB] hover:border-[#E5C158]/40"}`}>
                {s}x
              </button>
            ))}
          </div>
          {audioUrl && (
            <a href={audioUrl} download={`echo-${entry.id}.${entry.content_type === "audio/wav" ? "wav" : entry.content_type === "audio/mp4" ? "m4a" : "webm"}`} data-testid="entry-detail-download-btn" className="text-xs text-[#8E9BB4] hover:text-[#E5C158] inline-flex items-center gap-1.5">
              <Download size={14} /> {t("d_download")}
            </a>
          )}
        </div>
      </div>

      <section className="mt-12">
        <p className="label-gold mb-4">{t("d_transcript")}</p>
        <div className="border-l-2 border-[#E5C158]/40 pl-6">
          <p className="font-serif text-xl sm:text-2xl text-[#E8ECF4] leading-relaxed whitespace-pre-wrap" data-testid="entry-detail-transcript">
            {entry.transcript || t("d_empty_tr")}
          </p>
        </div>
      </section>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button data-testid="entry-detail-delete-btn" className="mt-16 inline-flex items-center gap-2 text-sm text-red-400/80 hover:text-red-400">
            <Trash2 size={15} /> {t("d_delete")}
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent className="bg-[#0C1322] border-[#E5C158]/20 text-[#F4F6FA]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">{t("d_delete_t")}</AlertDialogTitle>
            <AlertDialogDescription className="text-[#8E9BB4]">{t("d_delete_d")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="entry-detail-delete-cancel-btn" className="bg-transparent border-white/10 text-[#C2C9DB] hover:bg-white/5 hover:text-white">{t("d_cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="entry-detail-delete-confirm-btn" onClick={remove} className="bg-red-500 hover:bg-red-600 text-white">{t("d_confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
